import {
  LoaderCircle,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  eliminarCompraDefinitivamente,
  previsualizarEliminacionCompra,
} from '../../services/abastecimiento'
import {
  formatearEnteroCompra,
  formatearKilosCompra,
  formatearMontoCompra,
  MOTIVO_ELIMINACION_MAX,
  validarEliminacionCompra,
} from '../../utils/compras'

function Dato({
  etiqueta,
  valor,
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-0.5 break-words text-sm font-semibold text-slate-800">
        {valor || '—'}
      </p>
    </div>
  )
}

function Contador({
  etiqueta,
  valor,
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-center">
      <p className="text-lg font-bold tabular-nums text-slate-900">
        {formatearEnteroCompra(valor)}
      </p>

      <p className="text-xs text-slate-500">{etiqueta}</p>
    </div>
  )
}

function textoEstadoRecepcion(preview) {
  if (!preview.recepcion_id) return 'Sin recepción'

  return preview.estado_recepcion || 'Con recepción'
}

// Flujo completo de eliminación: consulta la previsualización al abrir,
// muestra el impacto y ejecuta la RPC. El padre solo controla qué compra
// está en eliminación y reacciona a onEliminada.
function EliminarCompraModal({
  compra,
  empresaId,
  onCancelar,
  onEliminada,
}) {
  const [preview, setPreview] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [errorPreview, setErrorPreview] = useState('')
  const [motivo, setMotivo] = useState('')
  const [codigo, setCodigo] = useState('')
  const [procesando, setProcesando] = useState(false)
  const [errorEliminar, setErrorEliminar] = useState('')

  // Guarda síncrona contra doble envío (el estado llega tarde al segundo clic).
  const enviandoRef = useRef(false)

  const compraId = compra?.id

  useEffect(() => {
    setPreview(null)
    setErrorPreview('')
    setErrorEliminar('')
    setMotivo('')
    setCodigo('')
    setProcesando(false)
    enviandoRef.current = false

    if (!compraId || !empresaId) return

    let cancelado = false

    setCargando(true)

    previsualizarEliminacionCompra({
      empresaId,
      compraId,
    })
      .then((resultado) => {
        if (cancelado) return

        if (!resultado) {
          setErrorPreview('No se obtuvo la previsualización de la compra.')
          return
        }

        setPreview(resultado)
      })
      .catch((error) => {
        if (cancelado) return

        console.error('Error al previsualizar eliminación:', error)
        setErrorPreview(
          error?.message ||
            'No fue posible consultar la compra a eliminar.'
        )
      })
      .finally(() => {
        if (!cancelado) setCargando(false)
      })

    return () => {
      cancelado = true
    }
  }, [compraId, empresaId])

  useEffect(() => {
    if (!compraId) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !procesando) {
        onCancelar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [compraId, procesando, onCancelar])

  if (!compra) return null

  const { motivoLimpio, errores, puedeConfirmar } =
    validarEliminacionCompra({
      preview,
      codigo,
      motivo,
    })

  const alHacerClicFuera = (event) => {
    if (procesando) return

    if (event.target === event.currentTarget) {
      onCancelar?.()
    }
  }

  const confirmar = async () => {
    if (enviandoRef.current || !puedeConfirmar) return

    enviandoRef.current = true
    setProcesando(true)
    setErrorEliminar('')

    try {
      const resultado = await eliminarCompraDefinitivamente({
        empresaId,
        compraId: compra.id,
        codigoConfirmacion: codigo,
        motivo: motivoLimpio,
      })

      if (!resultado || resultado.eliminada !== true) {
        throw new Error(
          'El servidor no confirmó la eliminación de la compra.'
        )
      }

      // Se mantiene la guarda activa: la compra ya no existe.
      setProcesando(false)
      onEliminada?.(resultado, preview)
    } catch (error) {
      console.error('Error al eliminar compra:', error)
      setErrorEliminar(
        error?.message || 'Ocurrió un error al eliminar la compra.'
      )
      enviandoRef.current = false
      setProcesando(false)
    }
  }

  const tieneInventario =
    preview &&
    (Number(preview.cantidad_movimientos) > 0 ||
      (preview.stock_actual_kg !== null &&
        preview.stock_actual_kg !== undefined))
  const motivosBloqueo = Array.isArray(preview?.motivos_bloqueo)
    ? preview.motivos_bloqueo
    : []
  const bloqueada = preview && preview.puede_eliminar !== true
  const stockNegativo =
    Number(preview?.stock_despues_eliminacion_kg) < 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="eliminar-compra-titulo"
        className="max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-h-[90vh] sm:rounded-2xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <Trash2 size={22} />
            </div>

            <h2
              id="eliminar-compra-titulo"
              className="text-lg font-bold text-slate-900"
            >
              Eliminar compra
            </h2>
          </div>

          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        {cargando && (
          <div className="flex min-h-40 items-center justify-center">
            <div className="text-center">
              <LoaderCircle
                size={30}
                className="mx-auto animate-spin text-emerald-600"
              />

              <p className="mt-3 text-sm font-medium text-slate-500">
                Analizando la compra...
              </p>
            </div>
          </div>
        )}

        {errorPreview && (
          <div
            role="alert"
            className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {errorPreview}
          </div>
        )}

        {preview && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Dato
                etiqueta="N° compra"
                valor={preview.codigo_compra}
              />

              <Dato
                etiqueta="Monto"
                valor={formatearMontoCompra(preview.total_compra)}
              />

              <Dato
                etiqueta="Modalidad"
                valor={preview.modalidad}
              />

              <Dato
                etiqueta="Recepción"
                valor={textoEstadoRecepcion(preview)}
              />

              <Dato
                etiqueta="Estado compra"
                valor={preview.estado_compra}
              />

              <Dato
                etiqueta="Kg netos"
                valor={formatearKilosCompra(preview.peso_neto_kg)}
              />
            </div>

            <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Se eliminarán
            </h3>

            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Contador
                etiqueta="Pesajes"
                valor={preview.cantidad_pesajes}
              />

              <Contador
                etiqueta="Recepciones"
                valor={preview.cantidad_recepciones}
              />

              <Contador
                etiqueta="Ajustes"
                valor={preview.cantidad_ajustes}
              />

              <Contador
                etiqueta="Movimientos"
                valor={preview.cantidad_movimientos}
              />
            </div>

            {tieneInventario && (
              <div className="mt-4 grid grid-cols-2 gap-4 rounded-xl border border-slate-200 p-4">
                <Dato
                  etiqueta="Stock actual"
                  valor={formatearKilosCompra(preview.stock_actual_kg)}
                />

                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Stock después de eliminar
                  </p>

                  <p
                    className={`mt-0.5 text-sm font-bold ${
                      stockNegativo ? 'text-red-600' : 'text-emerald-700'
                    }`}
                  >
                    {formatearKilosCompra(
                      preview.stock_despues_eliminacion_kg
                    )}
                  </p>
                </div>
              </div>
            )}

            {bloqueada && (
              <div
                role="alert"
                className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <p className="font-semibold">
                  Esta compra no se puede eliminar:
                </p>

                {motivosBloqueo.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {motivosBloqueo.map((motivoBloqueo) => (
                      <li key={motivoBloqueo}>{motivoBloqueo}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1">
                    El sistema no informó el motivo del bloqueo.
                  </p>
                )}
              </div>
            )}

            {!bloqueada && (
              <>
                <div className="mt-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <TriangleAlert
                    size={18}
                    className="mt-0.5 shrink-0"
                  />

                  <p className="font-semibold">
                    Esta acción eliminará definitivamente la compra y
                    todos sus registros logísticos asociados. No se puede
                    deshacer.
                  </p>
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  El sistema conserva únicamente una auditoría mínima de
                  la operación.
                </p>

                <div className="mt-5">
                  <label
                    htmlFor="eliminar-compra-motivo"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    Motivo de eliminación
                    <span className="ml-1 text-red-500">*</span>
                  </label>

                  <textarea
                    id="eliminar-compra-motivo"
                    value={motivo}
                    onChange={(event) => setMotivo(event.target.value)}
                    rows={3}
                    maxLength={MOTIVO_ELIMINACION_MAX}
                    placeholder="Ejemplo: Compra duplicada por error de digitación"
                    disabled={procesando}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
                  />

                  {motivo.length > 0 && errores.motivo && (
                    <p className="mt-2 text-sm text-red-600">
                      {errores.motivo}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <label
                    htmlFor="eliminar-compra-codigo"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    Escribe <span className="font-bold">{preview.codigo_compra}</span> para confirmar
                    <span className="ml-1 text-red-500">*</span>
                  </label>

                  <input
                    id="eliminar-compra-codigo"
                    type="text"
                    value={codigo}
                    onChange={(event) => setCodigo(event.target.value)}
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    disabled={procesando}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
                  />

                  {codigo.length > 0 && errores.codigo && (
                    <p className="mt-2 text-sm text-red-600">
                      {errores.codigo}
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {errorEliminar && (
          <div
            role="alert"
            className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {errorEliminar}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            className="min-h-12 w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={!puedeConfirmar || procesando}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-3 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Eliminando...' : 'Confirmar eliminación'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EliminarCompraModal
